/* global define, jQuery */
/* eslint-disable no-var, object-shorthand */
/**
 * Arrival: focus and the semantics that travel with it.
 *
 * Everything a visitor must hear when a step arrives is delivered by one act —
 * moving focus — and by whatever the focused element carries with it. That is
 * not a style choice: arriving also moves focus, and a screen reader flushes a
 * queued polite announcement when the focus change lands, so anything written
 * into a live region at that moment is never spoken however correct the DOM
 * ends up. Nothing here may write a live region.
 *
 * So this module does three things and nothing else: decide what receives
 * focus, make sure that element carries its position and its question, and
 * hand it the focus. Keeping it apart from the view is what lets those rules
 * be read — and changed — without the render, step, and theming code around
 * them.
 *
 * Like the step and theme modules, it holds no state and reaches into no view:
 * every function is given the step element it works on and the text the step's
 * position is currently reported as.
 */
define( 'controllers/focus',[], function () {
	/**
	 * Add an id to an element's ARIA token list without disturbing what
	 * is already there — a field may arrive with its own description.
	 *
	 * @param {Object} element   The element to describe.
	 * @param {string} attribute The token-list attribute to extend.
	 * @param {string} id        The id to add.
	 * @return {void}
	 */
	function appendAriaToken( element, attribute, id ) {
		if ( ! id ) {
			return;
		}

		var tokens = String( element.attr( attribute ) || '' )
			.split( /\s+/ )
			.filter( Boolean );

		if ( -1 === tokens.indexOf( id ) ) {
			tokens.push( id );
			element.attr( attribute, tokens.join( ' ' ) );
		}
	}

	/**
	 * Put an id at the front of an element's ARIA token list.
	 *
	 * The order of a token list is the order it is spoken, so this is how
	 * something is heard *before* what an element already says rather than
	 * after it. An id already in the list is moved rather than repeated, and
	 * a list that is empty can be seeded, because a control named by an
	 * ordinary `<label for>` has no token list of its own until one is made.
	 *
	 * @param {Object} element    The element to name.
	 * @param {string} attribute  The token-list attribute to extend.
	 * @param {string} id         The id to put first.
	 * @param {string} fallbackId Seeds an empty list so the element keeps a
	 *                            name of its own behind the new one.
	 * @return {void}
	 */
	function prependAriaToken( element, attribute, id, fallbackId ) {
		if ( ! id ) {
			return;
		}

		var tokens = String( element.attr( attribute ) || '' )
			.split( /\s+/ )
			.filter( Boolean );

		if ( ! tokens.length && fallbackId ) {
			tokens = [ fallbackId ];
		}

		tokens = tokens.filter( function ( token ) {
			return token !== id;
		} );

		tokens.unshift( id );
		element.attr( attribute, tokens.join( ' ' ) );
	}

	/**
	 * Whether an option already carries the question when it is spoken.
	 *
	 * The question counts as carried when it names the option itself, or
	 * when it names a group the option sits inside — entering that group
	 * voices its name, so a description would repeat it.
	 *
	 * @param {Object} option   The option input.
	 * @param {string} promptId The question element's id.
	 * @return {boolean} True when the question is already spoken.
	 */
	function alreadySpeaks( option, promptId ) {
		var named = '[aria-labelledby~="' + promptId + '"]';

		return Boolean( option.is( named ) || option.closest( named ).length );
	}

	/**
	 * Give the current step's choice options their question as context.
	 *
	 * A visitor arriving on an option has to know what the list decides,
	 * and has to be told exactly once. The question is spoken twice if it
	 * reaches the option through two channels at the same time, which is
	 * how it read on VoiceOver over Chrome when every option carried the
	 * question as a description on top of its group's name.
	 *
	 * Core does not render these lists the same way, so the channel that
	 * is already carrying the question differs by field type:
	 *
	 * - A radio option is labelled only by its own text (Email, Phone)
	 *   and the question sits on a wrapper no screen reader voices, so
	 *   nothing carries it. The immediate list wrapper becomes an
	 *   explicit radiogroup named by the question: unlike the generic
	 *   `group` the original finding came from, a radiogroup's name is
	 *   voiced on entry, and the list items it inherits are neutralised
	 *   with it so the group owns the radios rather than orphaned items.
	 * - A checkbox option is already labelled by the question and its own
	 *   text together, so the question is in the option's own name and
	 *   needs no second channel at all.
	 *
	 * The rule that covers both, and any list core renders differently
	 * later: describe an option with the question only when the question
	 * is not already in the option's name and not already the name of a
	 * group the option sits in.
	 *
	 * The markup is core's, only re-parented by this add-on, so it is
	 * decorated here at arrival rather than in a template; decoration at
	 * arrival also survives core re-rendering the field.
	 *
	 * @param {Object} active     The step container.
	 * @param {string} positionId The id of the element holding the position.
	 * @return {void}
	 */
	function decorateStepSemantics( active, positionId ) {
		var prompt = active.find( '.nf-field-label [id]' ).first();

		if ( ! prompt.length ) {
			return;
		}

		var promptId = prompt.attr( 'id' );
		var radios = active.find( 'input[type="radio"]' );
		var checks = active.find( '.listcheckbox-wrap input[type="checkbox"]' );

		var group = radios.closest( 'ul' );

		group.attr( 'role', 'radiogroup' ).attr( 'aria-labelledby', promptId );

		// The group is what a visitor enters before reaching any option, so
		// its name is where the position has to be for the step to announce
		// where it sits before it announces what it asks.
		prependAriaToken( group, 'aria-labelledby', positionId );

		// The radiogroup role replaces the `ul`'s own list role, which
		// leaves each `li` claiming to be a list item of a list that no
		// longer exists — announced as orphans, and owned by a role that
		// may not own them. Making the items presentational hands the
		// radiogroup the radios themselves as its children, which is what
		// it is allowed to own, and is done in the same operation so the
		// two can never be applied apart.
		group.children( 'li' ).attr( 'role', 'presentation' );

		radios.add( checks ).each( function ( index, input ) {
			var option = jQuery( input );

			if ( alreadySpeaks( option, promptId ) ) {
				return;
			}

			appendAriaToken( option, 'aria-describedby', promptId );
		} );
	}

	/**
	 * Give a statement step a name of its own, covering everything it says.
	 *
	 * Every other kind of step is announced from a name: a control's name, or
	 * the name of the group a choice sits in. A statement step was the last
	 * one announced the other way — a focused element with no role, read from
	 * its own text — and VoiceOver over Safari showed the limit of that rule:
	 * it reads the container's inline text, stops at the first block boundary,
	 * and offers the paragraph as something to interact with instead of
	 * reading it. The statement's content is never heard, on the one arrival
	 * that is nothing but content.
	 *
	 * So the statement is named too, by the mechanism that was just confirmed
	 * to be voiced on both pairings — an explicit role carrying an
	 * `aria-labelledby` chain, which is how the choice step's radiogroup
	 * speaks its question. The chain is position, then label, then content,
	 * which is the order every other arrival already announces, and it is
	 * computed from the elements themselves rather than copied out of them,
	 * so it cannot drift from what is on screen. A name is not a summary
	 * here: the content stays in the page and stays navigable, and the name
	 * is only what the arrival says.
	 *
	 * `region` is the role chosen for it, over the `group` an earlier round
	 * proved VoiceOver would not name. Only the step being read is ever in
	 * the accessibility tree — the others are `hidden` and `inert` — so this
	 * adds one named landmark describing where the visitor is, not one per
	 * step.
	 *
	 * @param {Object} active     The step container.
	 * @param {string} positionId The id of the element holding the position.
	 * @return {void}
	 */
	function nameStatementStep( active, positionId ) {
		var content = active.find( '.nf-field-element' ).first();

		if ( ! content.length ) {
			return;
		}

		if ( ! content.attr( 'id' ) ) {
			content.attr(
				'id',
				'nf-cf-statement-' + ( active.attr( 'id' ) || 'step' )
			);
		}

		var prompt = active.find( '.nf-field-label [id]' ).first();

		var names = [
			positionId,
			prompt.length ? prompt.attr( 'id' ) : '',
			content.attr( 'id' ),
		].filter( Boolean );

		active
			.attr( 'role', 'region' )
			.attr( 'aria-labelledby', names.join( ' ' ) );
	}

	/**
	 * Prefix a statement step's content with its position.
	 *
	 * This is the second channel behind the step's own name, and it is kept
	 * deliberately. If a pairing ignores the name and falls back to reading
	 * the focused element's text — which is what every pairing did before the
	 * step had a name — the position is still the first thing spoken rather
	 * than lost along with it. The span is not part of the name, so the two
	 * channels cannot both be heard: whichever one the screen reader takes,
	 * the arrival still opens with where the visitor is.
	 *
	 * It is kept in one screen-reader-only span, created on first arrival and
	 * rewritten in place after that, so going back never stacks copies.
	 *
	 * @param {Object} active       The step container.
	 * @param {string} positionText The step's position as it is reported.
	 * @return {void}
	 */
	function prefixStepPosition( active, positionText ) {
		var position = active.find( '.nf-cf__step-position' ).first();

		if ( ! position.length ) {
			position = jQuery( '<span>', {
				class: 'nf-cf__step-position screen-reader-text',
			} ).prependTo( active );
		}

		position.text( positionText ? positionText + '. ' : '' );
	}

	/**
	 * Let a control announce where the visitor is before what it asks.
	 *
	 * A description is always spoken after an element's name and role, so a
	 * position carried as a description arrives at the end — "What's your
	 * name, edit text, question 2 of 6" — while a statement step, which is
	 * read from its own text, announces its position first. Those are two
	 * different orders for the same journey, so the position is put in front
	 * of the control's name instead: "question 2 of 6, what's your name, edit
	 * text", matching the statement step.
	 *
	 * The name is only extended, never replaced. A control inside a group the
	 * position already names is left alone, since the group speaks first and
	 * repeating it on the control would say it twice. A control whose name
	 * cannot be located falls back to carrying the position as a description,
	 * which is heard late but is heard.
	 *
	 * @param {Object} active     The step container.
	 * @param {Object} target     The control that will take focus.
	 * @param {string} positionId The id of the element holding the position.
	 * @return {void}
	 */
	function nameWithPosition( active, target, positionId ) {
		if ( ! positionId ) {
			return;
		}

		if (
			target.closest( '[aria-labelledby~="' + positionId + '"]' ).length
		) {
			return;
		}

		var prompt = active.find( '.nf-field-label [id]' ).first();
		var promptId = prompt.length ? prompt.attr( 'id' ) : '';

		if ( ! target.attr( 'aria-labelledby' ) && ! promptId ) {
			appendAriaToken( target, 'aria-describedby', positionId );
			return;
		}

		prependAriaToken( target, 'aria-labelledby', positionId, promptId );
	}

	/**
	 * Move focus to the new step so the flow reads as one conversation.
	 *
	 * Everything that must be heard on arrival travels with the element
	 * that receives focus. A polite live region cannot carry any of it:
	 * arriving also moves focus, and a screen reader flushes the queued
	 * polite announcement when the focus change lands, so the write is
	 * never spoken however correct the DOM ends up.
	 *
	 * Every kind of step announces the same way: where the visitor is,
	 * then what is being asked, then what they are on — and every kind is
	 * announced from a name, because a focused element read from its own
	 * text was heard on one pairing and not another. A step with a control
	 * focuses the control and carries the position in front of the
	 * control's own name. A choice step carries it in front of the group's
	 * name, which is what a visitor enters before reaching an option. A
	 * statement step has no control, so the step container itself takes
	 * focus, named by its own position, label, and content.
	 *
	 * Focus is deferred until the step transition has finished; focusing
	 * something still animating in is unreliable.
	 *
	 * Focus is never taken from someone who is already on this step. The
	 * step list is rebuilt whenever conditional logic shows or hides a
	 * field, which an answer routinely does, and pulling focus back to the
	 * top of the step would interrupt the visitor mid-answer — it would
	 * take the keyboard out of a radio group the moment an arrow key
	 * selected something in it.
	 *
	 * @param {Object} active       The step container.
	 * @param {string} positionText The step's position as it is reported.
	 * @param {string} positionId   The id of the element holding that text.
	 * @return {void}
	 */
	function focusStep( active, positionText, positionId ) {
		var node = active.get( 0 );
		var focused = node ? node.ownerDocument.activeElement : null;
		var alreadyHere = Boolean(
			focused && ( node === focused || jQuery.contains( node, focused ) )
		);

		decorateStepSemantics( active, positionId );

		var target = active
			.find( '.nf-element:visible:not([type="hidden"])' )
			.first();
		var landing = target;

		if ( ! target.length ) {
			// A statement step has no control to land on, so the step
			// itself takes focus — otherwise focus falls to the document,
			// since start() has just hidden the welcome screen — and it
			// carries its own name, position first, with that name's text
			// prefixed into the step as well for any pairing that reads a
			// focused element rather than naming it.
			nameStatementStep( active, positionId );
			prefixStepPosition( active, positionText );
			active.attr( 'tabindex', '-1' );
			landing = active;
		} else {
			nameWithPosition( active, target, positionId );
		}

		if ( alreadyHere ) {
			return;
		}

		// Both kinds of step are focused by the one deferred call: the
		// delay is the step transition, which is the same either way, and
		// two copies of it would drift apart the moment one is tuned.
		setTimeout( function () {
			landing.trigger( 'focus' );
		}, 220 );
	}

	return {
		appendAriaToken: appendAriaToken,
		prependAriaToken: prependAriaToken,
		alreadySpeaks: alreadySpeaks,
		decorateStepSemantics: decorateStepSemantics,
		nameStatementStep: nameStatementStep,
		nameWithPosition: nameWithPosition,
		prefixStepPosition: prefixStepPosition,
		focusStep: focusStep,
	};
} );

